import React from 'react';
import { useEffect } from 'react';
import {
    Card, CardContent, Typography, Box,
    Tooltip, IconButton
} from '@mui/material';
import {
    AccountBalance as AccountBalanceIcon,
    Token as TokenIcon,
    AccountTree as ContractIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { TokenBalance, BalanceInfo, LPTokenInfo, AssetSummary } from '../../types/DashboardTypes';
import logger from '../../utils/logger';

// Import MEMER components
import { MemerCard, MemerCardContent } from '../ui/memer/MemerCard';
import LPTokenCard from './LPTokenCard';
import AssetSummaryCard from './AssetSummaryCard';

interface BalanceCardProps extends BalanceInfo {
    name: string;
    address: string;
    balance: string;
    type: 'multiSig' | 'contract' | 'token';
    chainId?: string;
    tokenSymbol?: string;
    tokenBalances?: TokenBalance[];
    lpTokens?: LPTokenInfo[];        // NEW
    assetSummary?: AssetSummary;     // NEW
    variant?: 'default' | 'compact';
}

const BalanceCard: React.FC<BalanceCardProps> = ({
    name,
    address,
    balance,
    type,
    tokenSymbol,
    tokenBalances = [],
    lpTokens = [],
    assetSummary,
    variant = 'default'
}) => {
    // Helper function to copy address to clipboard
    const copyToClipboard = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(address)
                .then(() => {
                    // Could add a toast notification here
                    logger.log("Address copied to clipboard");
                })
                .catch(err => {
                    logger.error("Error copying address:", err);
                });
        } else {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement("textarea");
            textArea.value = address;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            logger.log("Address copied to clipboard (fallback)");
        }
    };

    // Helper function to get the appropriate icon based on wallet type
    const getIcon = () => {
        switch (type) {
            case 'multiSig': return <AccountBalanceIcon color="primary" />;
            case 'token': return <TokenIcon color="secondary" />;
            case 'contract': return <ContractIcon color="info" />;
            default: return null;
        }
    };

    // Enhanced logging for debugging balance display issues
    useEffect(() => {
        logger.log(`[BalanceCard] ${name} - Props received:`, {
            address: address.substring(0, 10) + '...',
            balance: balance,
            tokenBalances: tokenBalances,
            type: type
        });

        if (tokenBalances && tokenBalances.length > 0) {
            logger.log(`[BalanceCard] ${name} has token balances:`, tokenBalances);
        }

        // Check if this is a pool contract that should have ETH
        const isPoolContract = name.toLowerCase().includes('pool') ||
            name.toLowerCase().includes('uniswap') ||
            name.toLowerCase().includes('aerodrome');

        if (isPoolContract) {
            logger.log(`[BalanceCard] Pool contract ${name} ETH balance: ${balance} ETH`);
            if (balance === '0' || balance === '0.0') {
                logger.warn(`[BalanceCard] WARNING: Pool contract ${name} shows 0 ETH balance - this may be incorrect`);
            }
        }
    }, [tokenBalances, name, balance, address, type]);

    // Render compact variant
    if (variant === 'compact') {
        return (
            <MemerCard hover={false}>
                <MemerCardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                            {name}
                        </Typography>
                        <Typography variant="caption">
                            {balance.substring(0, 6)} {tokenSymbol || 'ETH'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '80%'
                            }}>
                            {address.substring(0, 6)}...{address.substring(address.length - 4)}
                        </Typography>

                        <Tooltip title="Copy Address">
                            <IconButton size="small" onClick={copyToClipboard}>
                                <CopyIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Token balances */}
                    {tokenBalances && Array.isArray(tokenBalances) && tokenBalances.length > 0 && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Token Balances:
                            </Typography>
                            {tokenBalances.map((token, idx) => (
                                <Typography key={idx} variant="body2">
                                    {token.token || 'MEMER'}: {token.balance || '0'}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* LP Tokens and NFTs section */}
                    <LPTokenCard
                        lpTokens={lpTokens}
                        variant={variant}
                    />

                    {/* Asset Summary section */}
                    {assetSummary && (
                        <AssetSummaryCard
                            assetSummary={assetSummary}
                            variant={variant}
                        />
                    )}
                </MemerCardContent>
            </MemerCard>
        );
    }

    // Render default variant with MEMER styling
    return (
        <MemerCard>
            <MemerCardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getIcon()}
                        {name}
                    </Typography>
                    <Tooltip title="Copy Address">
                        <IconButton size="small" onClick={copyToClipboard}>
                            <CopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {address}
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" fontWeight="bold">
                        ETH Balance: {balance} ETH
                    </Typography>
                </Box>

                {/* Multi-asset balances section */}
                {tokenBalances && tokenBalances.length > 0 && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Asset Balances:
                        </Typography>
                        {tokenBalances.map((token, idx) => {
                            // Format balance display based on token type
                            const isNFT = token.token.includes('POS') || token.token.includes('NFT');
                            const isLP = token.token.includes('LP') || token.token.includes('AERO') || token.token.includes('POOL');

                            let displayValue = token.balance || '0';
                            let displayUnit = '';

                            if (isNFT) {
                                displayUnit = token.balance === '1' ? ' NFT' : ' NFTs';
                            } else if (isLP) {
                                displayUnit = ' LP';
                            }

                            // Format large numbers
                            const numBalance = parseFloat(displayValue);
                            if (!isNFT && !isLP && numBalance > 1000000) {
                                displayValue = (numBalance / 1000000).toFixed(2) + 'M';
                            } else if (!isNFT && !isLP && numBalance > 1000) {
                                displayValue = (numBalance / 1000).toFixed(2) + 'K';
                            } else if (!isNFT && numBalance > 0 && numBalance < 0.001) {
                                displayValue = numBalance.toExponential(2);
                            } else if (!isNFT && !isLP) {
                                displayValue = numBalance.toFixed(6).replace(/\.?0+$/, '');
                            } else if (isLP) {
                                // For LP tokens, show more precision
                                displayValue = numBalance.toFixed(8).replace(/\.?0+$/, '');
                            }

                            return (
                                <Typography key={idx} variant="body2" sx={{
                                    color: numBalance > 0 ? 'text.primary' : 'text.secondary',
                                    fontWeight: numBalance > 0 ? 'medium' : 'normal',
                                    backgroundColor: isLP && numBalance > 0 ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                                    padding: isLP && numBalance > 0 ? '2px 4px' : '0',
                                    borderRadius: isLP && numBalance > 0 ? '4px' : '0'
                                }}>
                                    {token.token || 'UNKNOWN'}: {displayValue}{displayUnit}
                                </Typography>
                            );
                        })}
                    </Box>
                )}

                {/* LP Tokens and NFTs section */}
                <LPTokenCard
                    lpTokens={lpTokens}
                    variant={variant}
                />

                {/* Asset Summary section */}
                {assetSummary && (
                    <AssetSummaryCard
                        assetSummary={assetSummary}
                        variant={variant}
                    />
                )}
            </MemerCardContent>
        </MemerCard>
    );
};

export default BalanceCard;
